import React, { useState, useEffect } from 'react';
import {
  Upload, Search, Grid, List, Image, Video, Music, FileText,
  Folder, Hash, Trash2, Download, Eye,
  CheckSquare, Square, Move, Tag as TagIcon
} from 'lucide-react';
import { useAssetsStore, MediaAsset } from '../../store/assetsStore';
import { useAuthStore } from '../../store/authStore';
import UploadAssetModal from './UploadAssetModal';
import ViewAssetModal from './ViewAssetModal';

const AssetLibraryPage: React.FC = () => {
  const {
    assets,
    isLoading,
    searchTerm,
    filterCategory,
    selectedAssets,
    viewingAsset,
    setSearchTerm,
    setFilterCategory,
    setViewingAsset,
    toggleAssetSelection,
    clearSelection,
    selectAll,
    fetchAssets,
    deleteAsset,
    deleteAssets,
    downloadAsset,
    moveAssets,
    tagAssets
  } = useAssetsStore();
  const { user } = useAuthStore();

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  // Helper functions
  const getCategoryFromMimeType = (mimeType: string): string => {
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    if (mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('text')) return 'document';
    if (mimeType.includes('logo') || mimeType.includes('brand')) return 'logo';
    return 'other';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();

    // Reset time to midnight for accurate day comparison
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const uploadDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const diffTime = today.getTime() - uploadDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  // Render preview based on file type
  const renderPreview = (asset: MediaAsset) => {
    const category = getCategoryFromMimeType(asset.mimeType);
    const url = asset.thumbnailUrl || asset.cdnUrl;

    if (category === 'image' && url) {
      return (
        <img
          src={url}
          alt={asset.altText || asset.originalName}
          className="w-full h-full object-cover"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
          }}
        />
      );
    }

    if (category === 'video' && url) {
      return (
        <video
          src={url}
          className="w-full h-full object-cover"
          controls={false}
          muted
          playsInline
        />
      );
    }

    if (category === 'audio' && url) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-100 to-pink-100">
          <Music className="w-16 h-16 text-purple-600" />
        </div>
      );
    }

    if (category === 'document' && url) {
      if (asset.mimeType.includes('pdf')) {
        return (
          <iframe
            src={url}
            className="w-full h-full"
            title={asset.originalName}
          />
        );
      }
      return (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-100 to-indigo-100">
          <FileText className="w-16 h-16 text-primary-600" />
        </div>
      );
    }

    // Fallback icon
    return (
      <div className="text-gray-400">
        {category === 'image' && <Image className="w-12 h-12 mx-auto" />}
        {category === 'video' && <Video className="w-12 h-12 mx-auto" />}
        {category === 'audio' && <Music className="w-12 h-12 mx-auto" />}
        {category === 'document' && <FileText className="w-12 h-12 mx-auto" />}
      </div>
    );
  };

  // Category definitions
  const categories = [
    { id: 'all', label: 'All Assets', icon: Folder, count: assets.length },
    {
      id: 'image',
      label: 'Images',
      icon: Image,
      count: assets.filter(a => getCategoryFromMimeType(a.mimeType) === 'image').length
    },
    {
      id: 'video',
      label: 'Videos',
      icon: Video,
      count: assets.filter(a => getCategoryFromMimeType(a.mimeType) === 'video').length
    },
    {
      id: 'audio',
      label: 'Audio',
      icon: Music,
      count: assets.filter(a => getCategoryFromMimeType(a.mimeType) === 'audio').length
    },
    {
      id: 'document',
      label: 'Documents',
      icon: FileText,
      count: assets.filter(a => getCategoryFromMimeType(a.mimeType) === 'document').length
    },
    {
      id: 'logo',
      label: 'Logos',
      icon: Image,
      count: assets.filter(a => getCategoryFromMimeType(a.mimeType) === 'logo').length
    }
  ];

  // Filter assets
  const filteredAssets = assets.filter(asset => {
    const matchesSearch = !searchTerm ||
      asset.originalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory = filterCategory === 'all' ||
      getCategoryFromMimeType(asset.mimeType) === filterCategory;

    const matchesFolder = true; // Folder filtering can be added later if needed

    return matchesSearch && matchesCategory && matchesFolder;
  });

  // Get unique tags
  const allTags = Array.from(new Set(assets.flatMap(a => a.tags))).slice(0, 10);

  // Render grid view
  const renderGridView = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      {filteredAssets.map(asset => (
        <div
          key={asset.id}
          className={`group relative bg-white dark:bg-gray-800 rounded-3xl overflow-hidden cursor-pointer transition-all duration-200 ${selectedAssets.includes(asset.id)
            ? 'border-2 border-primary-500 shadow-[0_0_20px_rgba(0,0,0,0.1)]'
            : 'border border-gray-100 dark:border-gray-700 shadow-[0_0_15px_rgba(0,0,0,0.07)] hover:shadow-[0_0_20px_rgba(0,0,0,0.1)]'
            }`}
          onClick={() => {
            if (selectedAssets.length > 0) {
              toggleAssetSelection(asset.id);
            } else {
              setViewingAsset(asset);
            }
          }}
        >
          {/* Selection checkbox */}
          <div
            className="absolute top-2 left-2 z-10"
            onClick={(e) => {
              e.stopPropagation();
              toggleAssetSelection(asset.id);
            }}
          >
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${selectedAssets.includes(asset.id)
              ? 'bg-primary-500 border-primary-500'
              : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 group-hover:border-primary-400'
              }`}>
              {selectedAssets.includes(asset.id) && (
                <CheckSquare className="w-4 h-4 text-white" />
              )}
            </div>
          </div>

          {/* Preview */}
          <div className="aspect-square bg-gray-100 dark:bg-gray-700 flex items-center justify-center relative overflow-hidden">
            {renderPreview(asset)}

            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity flex items-center justify-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setViewingAsset(asset);
                }}
                className="opacity-0 group-hover:opacity-100 p-2 bg-white rounded-lg hover:bg-gray-100 transition-all"
              >
                <Eye className="w-4 h-4 text-gray-700" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  downloadAsset(asset.id);
                }}
                className="opacity-0 group-hover:opacity-100 p-2 bg-white rounded-lg hover:bg-gray-100 transition-all"
              >
                <Download className="w-4 h-4 text-gray-700" />
              </button>
            </div>
          </div>

          {/* Info */}
          <div className="p-3">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={asset.originalName}>
              {asset.originalName}
            </p>
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(asset.fileSize)}</span>
              <span className="text-xs text-gray-400 dark:text-gray-500 uppercase">
                {asset.mimeType.split('/')[1]?.split(';')[0] || 'file'}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Render list view
  const renderListView = () => (
    <div className="space-y-2">
      {filteredAssets.map(asset => (
        <div
          key={asset.id}
          className={`flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-3xl transition-all cursor-pointer ${selectedAssets.includes(asset.id)
            ? 'border-2 border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-[0_0_20px_rgba(0,0,0,0.1)]'
            : 'border border-gray-100 dark:border-gray-700 shadow-[0_0_15px_rgba(0,0,0,0.07)] hover:shadow-[0_0_20px_rgba(0,0,0,0.1)]'
            }`}
          onClick={() => {
            if (selectedAssets.length > 0) {
              toggleAssetSelection(asset.id);
            } else {
              setViewingAsset(asset);
            }
          }}
        >
          {/* Selection checkbox */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              toggleAssetSelection(asset.id);
            }}
          >
            {selectedAssets.includes(asset.id) ? (
              <CheckSquare className="w-5 h-5 text-primary-500" />
            ) : (
              <Square className="w-5 h-5 text-gray-300" />
            )}
          </div>

          {/* Thumbnail */}
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
            {(() => {
              const category = getCategoryFromMimeType(asset.mimeType);
              const url = asset.thumbnailUrl || asset.cdnUrl;

              if (category === 'image' && url) {
                return (
                  <img
                    src={url}
                    alt={asset.altText || asset.originalName}
                    className="w-full h-full object-cover"
                  />
                );
              }
              if (category === 'video' && url) {
                return (
                  <video
                    src={url}
                    className="w-full h-full object-cover"
                    muted
                    playsInline
                  />
                );
              }
              return (
                <div className="w-full h-full flex items-center justify-center">
                  {category === 'image' && <Image className="w-8 h-8 text-gray-400" />}
                  {category === 'video' && <Video className="w-8 h-8 text-gray-400" />}
                  {category === 'audio' && <Music className="w-8 h-8 text-gray-400" />}
                  {category === 'document' && <FileText className="w-8 h-8 text-gray-400" />}
                </div>
              );
            })()}
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-gray-900 dark:text-white truncate">{asset.originalName}</h3>
            <div className="flex items-center gap-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
              <span>{formatFileSize(asset.fileSize)}</span>
              <span className="uppercase">{asset.mimeType.split('/')[1]?.split(';')[0] || 'file'}</span>
              <span>{formatDate(asset.createdAt)}</span>
            </div>
            <div className="flex items-center gap-2 mt-1 text-xs text-gray-400 dark:text-gray-500">
              <span>by {user && asset.uploadedBy === user.id
                ? `${user.firstName} ${user.lastName}`
                : 'Unknown'}</span>
            </div>
            {asset.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {asset.tags.slice(0, 3).map(tag => (
                  <span key={tag} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs">
                    #{tag}
                  </span>
                ))}
                {asset.tags.length > 3 && (
                  <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs">
                    +{asset.tags.length - 3}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setViewingAsset(asset);
              }}
              className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                downloadAsset(asset.id);
              }}
              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                deleteAsset(asset.id);
              }}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  if (isLoading && assets.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Asset Library</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Centralized repository for your digital assets</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-4 gap-4 w-full max-w-2xl">
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Assets</div>
            <div className="text-xl font-bold text-gray-900 dark:text-white text-center break-all">
              {assets.length}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">Images</div>
            <div className="text-xl font-bold text-primary-600 dark:text-primary-400 text-center break-all">
              {categories.find(c => c.id === 'image')?.count || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">Videos</div>
            <div className="text-xl font-bold text-purple-600 dark:text-purple-400 text-center break-all">
              {categories.find(c => c.id === 'video')?.count || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">Storage</div>
            <div className="text-xl font-bold text-green-600 dark:text-green-400 text-center break-all">
              {formatFileSize(assets.reduce((sum, a) => sum + a.fileSize, 0))}
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search assets, tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
              />
            </div>
            <div className="flex gap-2 min-w-0">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 transition-colors min-w-[150px] max-w-full"
              >
                <option value="all">All Categories</option>
                {categories.filter(c => c.id !== 'all').map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'grid' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
              >
                <Grid className="w-4 h-4" />
                Grid
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
              >
                <List className="w-4 h-4" />
                List
              </button>
            </div>
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-xl hover:from-primary-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Upload className="w-4 h-4" />
              Upload
            </button>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedAssets.length > 0 && (
          <div className="mt-4 p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
                {selectedAssets.length} item{selectedAssets.length !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={clearSelection}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                Clear
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  const folder = prompt('Enter folder name:');
                  if (folder) moveAssets(selectedAssets, folder);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <Move className="w-4 h-4" />
                Move
              </button>
              <button
                onClick={() => {
                  const tagsInput = prompt('Enter tags (comma-separated):');
                  if (tagsInput) {
                    const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean);
                    tagAssets(selectedAssets, tags);
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <TagIcon className="w-4 h-4" />
                Tag
              </button>
              <button
                onClick={() => {
                  if (confirm(`Delete ${selectedAssets.length} asset(s)?`)) {
                    deleteAssets(selectedAssets);
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Categories */}
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Categories</h3>
            <div className="space-y-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setFilterCategory(category.id)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${filterCategory === category.id
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-700'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                    }`}
                >
                  <div className="flex items-center gap-3">
                    <category.icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{category.label}</span>
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">● {category.count}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tags */}
          {allTags.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {allTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => setSearchTerm(tag)}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Hash className="w-3 h-3" />
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Assets</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={selectAll}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                >
                  Select All
                </button>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {filteredAssets.length} asset{filteredAssets.length !== 1 ? 's' : ''} found
                </span>
              </div>
            </div>

            {filteredAssets.length === 0 ? (
              <div className="text-center py-12">
                <Folder className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No assets found</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {searchTerm || filterCategory !== 'all'
                    ? 'Try adjusting your search or filters'
                    : 'Get started by uploading your first asset'
                  }
                </p>
                {!searchTerm && filterCategory === 'all' && (
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Your First Asset
                  </button>
                )}
              </div>
            ) : (
              viewMode === 'grid' ? renderGridView() : renderListView()
            )}
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadAssetModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
        />
      )}

      {/* View Asset Modal */}
      {viewingAsset && (
        <ViewAssetModal
          isOpen={!!viewingAsset}
          onClose={() => setViewingAsset(null)}
          asset={viewingAsset}
        />
      )}
    </div>
  );
};

export default AssetLibraryPage;

